import { supabase } from '../lib/supabaseClient';
import type { ProductionBatch, UUID } from '../types/domain';

/**
 * Production batch adapter (FASE 7, migration 0039). production_batches is
 * branch+warehouse scoped (unlike menu/recipe) — branchId, when passed,
 * scopes the read to that branch's own batches, same convention as
 * api/purchasing.ts's listGoodsReceipts(branchId?).
 */

export async function listProductionBatches(branchId?: UUID): Promise<ProductionBatch[]> {
  let query = supabase
    .from('production_batches')
    .select('batch_id, tenant_id, branch_id, warehouse_id, recipe_version_id, batch_number, qty_produced, status, produced_by, posted_at, created_at')
    .order('created_at', { ascending: false });
  if (branchId) query = query.eq('branch_id', branchId);
  const { data, error } = await query;
  if (error) throw new Error(`listProductionBatches failed: ${error.message}`);
  return (data ?? []) as ProductionBatch[];
}

/** Calls fn_create_production_batch (migration 0039, permission-gated on production_batch.create, branch-scoped). Leaves the batch as DRAFT. */
export async function createProductionBatch(
  branchId: UUID,
  warehouseId: UUID,
  recipeVersionId: UUID,
  qtyProduced: number
): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_create_production_batch', {
    p_branch_id: branchId,
    p_warehouse_id: warehouseId,
    p_recipe_version_id: recipeVersionId,
    p_qty_produced: qtyProduced,
  });
  if (error) throw new Error(`fn_create_production_batch failed: ${error.message}`);
  return data as UUID;
}

/**
 * Calls fn_post_production_batch_checked (migration 0039, permission-gated
 * on production_batch.post, branch-scoped) — the thin wrapper over the
 * already-tested fn_post_production_batch. Writes one ISSUE row per
 * ingredient to stock_ledger; idempotent (a DRAFT-status guard rejects any
 * second post attempt on the same batch).
 */
export async function postProductionBatch(batchId: UUID): Promise<void> {
  const { error } = await supabase.rpc('fn_post_production_batch_checked', { p_batch_id: batchId });
  if (error) throw new Error(`fn_post_production_batch_checked failed: ${error.message}`);
}
